import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_slice_generation, use_slice_ui } from '@/hooks/store/use_slices';
import { useToast } from '@/hooks/use-toast';
import { ENUM__i18n_namespace } from '@/services/i18n/ENUM__i18n_namespace';
import { LOCAL__ui_set_boolean } from '@/state/ui/_base/LOCAL__ui_set_boolean';
import { LOCAL__ui_drag_and_drop_set_hovered_graph_field } from '@/state/ui/drag_and_drop/LOCAL__ui_drag_and_drop_set_hovered_graph_field';
import { FN_IMAGE_FILE_DATA__upload_to_workspace_and_set_to_graph } from '@/utils/_methods/FN_IMAGE_FILE_DATA__upload_to_workspace_and_set_to_graph';
import { create_file_data } from '@/utils/utils_upload';
import { assert_not_null } from '@shared/asserts/utils_asserts';
import { FIELD__image_asset } from '@shared/dto/generation/graph_invoke/_fields/image/FIELD__image_asset';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { G_DRAG_AND_DROP__area_with_label } from './G_DRAG_AND_DROP__area_with_label';

interface PROPS {
  field: FIELD__image_asset;
}

export function G_GRAPH__DRAG_AND_DROP__image_external({ field }: PROPS) {
  /*
   * STORE
   */

  const dispatch = use_dispatch();
  const { toast } = useToast();
  const { t: t_base_ui } = useTranslation(ENUM__i18n_namespace.BASE_UI);

  const { generation_current } = use_slice_generation(['generation_current']);

  const { drag_and_drop__image_external_zones_show, drag_and_drop__image_external_zone_hovered_graph_field, g_general__ui_disabled } = use_slice_ui([
    'drag_and_drop__image_external_zones_show',
    'drag_and_drop__image_external_zone_hovered_graph_field',
    'g_general__ui_disabled',
  ]);

  /*
   * STATE
   */

  let { graph } = generation_current.tool;

  const is_hovered = useMemo(
    () => drag_and_drop__image_external_zone_hovered_graph_field.meta.field_id === field.meta.field_id,
    [field, drag_and_drop__image_external_zone_hovered_graph_field],
  );

  /*
   * EVENTS
   */

  function onDrop(e: React.DragEvent): void {
    e.preventDefault();
    e.stopPropagation();

    void upload_file(e.dataTransfer.files);

    dispatch(LOCAL__ui_set_boolean({ key: 'drag_and_drop__image_external_zones_show', value: false }));
    dispatch(LOCAL__ui_drag_and_drop_set_hovered_graph_field());
  }

  /*
   * METHODS
   */

  async function upload_file(files_list: FileList) {
    const file = files_list[0];
    assert_not_null(file);

    let file_data = await create_file_data(toast, file, t_base_ui);

    FN_IMAGE_FILE_DATA__upload_to_workspace_and_set_to_graph(file_data, field);
  }

  function get_class_name_opacity() {
    return drag_and_drop__image_external_zones_show ? ' opacity-100 ' : ' opacity-0 ';
  }

  function is_interactive() {
    let out = true;

    if (!drag_and_drop__image_external_zones_show) {
      out = false;
    } else if (!field.is_category_image) {
      out = false;
    } else if (!field.is_ready_for_manual_edit()) {
      out = false;
    }

    return out;
  }

  function get_text() {
    let out = '';

    if (field.is_category_image) {
      out = 'Not for external images';
    } else if (!field.is_ready_for_manual_edit()) {
      out = 'Read Only Field';
    } else {
      out = 'Drop Image Here';
    }

    return out;
  }

  /*
   * RENDERS
   */

  /*
   * HTML
   */
  // if (!drag_and_drop__image_external_zones_show) return null;
  // if (!field.field_image_main.get_can_be_changed_manually(generation_current.graph))
  //   return <G_DRAG_AND_DROP__area_with_label text="Read Only" not_interactive={true} />;

  // todo: use animation border instead of static
  //todo: animate on hover

  if (g_general__ui_disabled) return null;

  return (
    <div
      data-drag-and-drop-zone-graph-field={field.meta.field_id}
      {...{ onDrop }}
      className={`info_drag_and_drop_image_external
    absolute inset-0 z-50
    flex-center p-4
    text-center text-white 
    rounded-lg
    ${get_class_name_opacity()}
    ${is_interactive() ? ' border border-2 border-dashed border-component-border ' : ' pointer-events-none bg-black '}
  `}
    >
      {/* content */}
      <G_DRAG_AND_DROP__area_with_label text={get_text()} class_name="bg-white/10" />
    </div>
  );
}
